import { useCallback, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { replyAddressInput } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Button } from '../ui/Button';
import { REPLY_ADDRESS_ANCHOR } from '../tickets/ticketView';
import { useLanding } from '../ui/useLanding';

import { fieldErrorOf } from '../../lib/field-error';
export { fieldErrorOf } from '../../lib/field-error';

/**
 * "Customer replies go to" (MAP 249 spec §5): the Reply-To on the shop's customer emails, used
 * only once someone holding the address has clicked the link ServWell emails to it. Owner only,
 * like the rest of the profile (the server's `org:manage`); the office reads it.
 *
 * What it shows is the server's (`replyAddress.status`): the CONFIRMED address in the field, and
 * under it the newest one still waiting for its click ("Waiting for you to confirm — check …",
 * "Send the link again"). The previous confirmed address keeps working until then. Saving a new
 * address sends its link (`replyAddress.request`); saving it blank takes the address off
 * (`replyAddress.clear`). A refusal from the server is shown under the field, in its words.
 *
 * An owner whose own sign-up email is not confirmed reads that here too, with "Send it again"
 * (MAP 249 spec §11b): a shop can email nobody, this link included, until an owner confirms.
 *
 * ⭐ The composer's "Add a reply address in Settings" links here (`#customer-replies`): the
 * field is scrolled to, marked for `LANDING_HIGHLIGHT_MS`, and (for the owner) focused, once
 * per arrival (`useLanding`). The router's own hash scroll misses it when the card's read answers
 * after the navigation, so the field lands itself. It lives in Settings → Email since ticket 07,
 * under the same anchor.
 */
/**
 * How often the field asks again while a link is waiting for its click (MAP 249 11b QA, D2): the
 * link is clicked in ANOTHER tab or on a phone, and this tab, left open on Settings, must not keep
 * saying "Waiting for you to confirm" once the address is in.
 */
export const REPLY_PENDING_REFRESH_MS = 30_000;

export function CustomerRepliesField({ owner }: { owner: boolean }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  /**
   * - `refetchOnMount: 'always'`: the cache is persisted, so a reload (or coming back to
   *   Settings) inside the app's 30-second `staleTime` would show the answer from BEFORE the
   *   link was clicked elsewhere, and never ask (QA 11b, D2).
   * - `refetchInterval` while a link waits: the click happens outside this tab.
   */
  const status = useQuery({
    ...trpc.replyAddress.status.queryOptions(),
    refetchOnMount: 'always',
    refetchInterval: (query) =>
      query.state.data?.pending ? REPLY_PENDING_REFRESH_MS : false,
  });
  const mine = useQuery({ ...trpc.me.emailConfirmation.queryOptions(), enabled: owner });
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const inputId = useId();
  const helpId = useId();
  const errorId = useId();
  const done = (sentence: string | null) => {
    setError(null);
    setDraft(null);
    setNote(sentence);
    void qc.invalidateQueries();
  };
  const request = useMutation(
    trpc.replyAddress.request.mutationOptions({
      onSuccess: (r) =>
        done(r.sent ? null : "ServWell couldn't send the link just now. Send it again."),
      onError: (e) => setError(fieldErrorOf(e.message)),
    }),
  );
  const clear = useMutation(
    trpc.replyAddress.clear.mutationOptions({
      onSuccess: () => done(null),
      onError: (e) => setError(fieldErrorOf(e.message)),
    }),
  );
  const resend = useMutation(
    trpc.me.resendEmailConfirmation.mutationOptions({
      onSuccess: (r) => done(`We sent a new link to ${r.sentTo}.`),
      onError: (e) => setError(e.message),
    }),
  );
  const busy = request.isPending || clear.isPending || resend.isPending;

  const input = useRef<HTMLInputElement>(null);
  const landing = useLanding<HTMLDivElement>(
    REPLY_ADDRESS_ANCHOR,
    useCallback(() => {
      if (owner) input.current?.focus({ preventScroll: true });
    }, [owner]),
  );

  const saved = status.data?.confirmed?.email ?? null;
  const pending = status.data?.pending ?? null;
  const current = draft ?? saved ?? '';
  const changed = draft !== null && draft.trim() !== (saved ?? '');
  const submit = () => {
    if (!changed || busy) return;
    if (draft.trim() === '') {
      setError(null);
      clear.mutate();
      return;
    }
    const checked = replyAddressInput.safeParse({ email: draft });
    if (!checked.success) {
      setError(checked.error.issues[0]?.message ?? 'Enter one email address');
      return;
    }
    setError(null);
    request.mutate({ email: checked.data.email! });
  };

  return (
    <div
      ref={landing.ref}
      id={REPLY_ADDRESS_ANCHOR}
      data-landed={landing.lit ? 'true' : undefined}
      className={`scroll-mt-[var(--sw-anchor-offset)] p-1 transition-colors ${landing.mark}`}
    >
      <label htmlFor={inputId} className="text-sm font-medium">
        Customer replies go to
      </label>
      <p id={helpId} className="text-xs text-muted-foreground">
        Customers’ replies to ticket, invoice and quote emails, reminders and follow-ups
        go here, once you confirm the address from the link we email to it.
      </p>
      {/* `noValidate`: the browser's own email check would stop the submit with its bubble,
          and the refusal belongs under the field, in the server's words (same rule). */}
      <form
        noValidate
        className="mt-1 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          ref={input}
          id={inputId}
          type="email"
          inputMode="email"
          autoComplete="email"
          className="h-10 min-w-0 flex-1 basis-56 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm aria-[invalid=true]:border-[var(--sw-danger-text)]"
          value={current}
          placeholder="office@yourshop.com"
          disabled={!owner}
          // Not `disabled` while saving or loading: that drops the focus to <body> (LESSONS
          // 2026-09-28).
          readOnly={busy || !status.data}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${helpId} ${errorId}` : helpId}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
            setNote(null);
          }}
        />
        {changed ? (
          <Button type="submit" aria-disabled={busy || undefined}>
            {request.isPending || clear.isPending ? 'Saving…' : 'Save'}
          </Button>
        ) : null}
      </form>
      {error ? (
        <p
          id={errorId}
          role="alert"
          className="mt-1 text-xs text-[var(--sw-danger-text)]"
        >
          {error}
        </p>
      ) : null}
      {note ? (
        <p role="status" className="mt-1 text-xs text-muted-foreground">
          {note}
        </p>
      ) : null}
      {pending && !changed ? (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">
            {pending.expired
              ? `The link to ${pending.email} expired.`
              : `Waiting for you to confirm — check ${pending.email}.`}
            {saved ? ` Until then, replies go to ${saved}.` : ''}
          </span>
          {owner ? (
            <Button
              variant="ghost"
              size="sm"
              aria-disabled={busy || undefined}
              onClick={() => {
                if (!busy) request.mutate({ email: pending.email });
              }}
            >
              Send the link again
            </Button>
          ) : null}
        </div>
      ) : null}
      {status.data && !saved && !pending && !changed ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Not set, so customer emails go without one, and messages written for a customer
          on a ticket wait until an address is confirmed.
        </p>
      ) : null}
      {owner && mine.data && !mine.data.confirmed ? (
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">
            Confirm your own email address first: we emailed a link to {mine.data.email}.
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-disabled={busy || undefined}
            onClick={() => {
              if (!busy) resend.mutate();
            }}
          >
            Send it again
          </Button>
        </div>
      ) : null}
      {!owner ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Only the owner can change this.
        </p>
      ) : null}
    </div>
  );
}
